<script setup>
import { ref, provide, computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { onMounted, onUnmounted } from 'vue'
import { useUserInteraction } from '@/composables/useUserInteraction.js'
import FlyoutMenu from "@/Components/Common/FlyoutMenu.vue";
import {useExternalRedirect} from "@/composables/useExternalRedirect";
import Toast from '@/Components/Common/Toast.vue'

import NsfwPopUp from "@/Components/Common/NsfwPopUp.vue";

// GTM Configuration and History Change Fix is now handled in the Blade template (app2.blade.php)

// Additionally, ensure on the frontend that GTM's history change events are filtered once app-wide
function ensureGtmHistoryChangeFilter() {
    try {
        // Ensure dataLayer exists
        window.dataLayer = window.dataLayer || []
        // Avoid double-wrapping
        if (window.__gtmHistoryFiltered === true) return

        const originalPush = window.dataLayer.push && window.dataLayer.push.bind
            ? window.dataLayer.push.bind(window.dataLayer)
            : null
        if (!originalPush) return

        // Preserve the original once for optional future restore/debugging
        if (!window.__gtmOriginalPush) {
            window.__gtmOriginalPush = originalPush
        }

        window.dataLayer.push = function () {
            try {
                const item = arguments[0]
                if (item && typeof item === 'object' && typeof item.event === 'string') {
                    const ev = item.event
                    if (ev === 'gtm.historyChange' || ev === 'gtm.historyChange-v2' || ev === 'gtm.historyChange-v3') {
                        if ('oldHistoryState' in item) delete item.oldHistoryState
                        if ('newHistoryState' in item) delete item.newHistoryState
                    }
                    if (ev === 'gtm.oldHistoryState' || ev === 'gtm.newHistoryState') {
                        item.event = 'gtm.historyStateFiltered'
                        if ('oldHistoryState' in item) delete item.oldHistoryState
                        if ('newHistoryState' in item) delete item.newHistoryState
                    }
                }
            } catch (e) {}
            return originalPush.apply(window.dataLayer, arguments)
        }

        window.__gtmHistoryFiltered = true
    } catch (e) {}
}

const { hasInteracted } = useUserInteraction()
const { redirectTo } = useExternalRedirect();

// Make composables available to all child components
provide('hasInteracted', hasInteracted)
provide('redirectTo', redirectTo)

const pathName = window.location.pathname
const user = usePage()?.props?.auth?.user
const { props } = usePage()

const isFeedPage = computed(() => usePage().component === 'Reels/FeedV2')

// Computed property to check if the user is logged in
const isLoggedIn = ref(props?.auth && props?.auth.user && props?.auth.user.name !== null)

// Toast state
const showToast = ref(false)
const toastText = ref('')
const toastIcon = ref('fas fa-circle-info')

// Handle toast events
const handleToastEvent = (event) => {
    const { message, icon, duration } = event.detail
    toastText.value = message
    toastIcon.value = icon || 'fas fa-circle-info'
    showToast.value = true

    setTimeout(() => {
        showToast.value = false
    }, duration || 5000)
}


onMounted(() => {
    isPWA()
    document.body.addEventListener('wheel', preventHorizontalScroll, { passive: true })
    window.addEventListener('popstate', removeModalBackdrop)
    window.addEventListener('show-toast', handleToastEvent)
    // Apply GTM history change filter app-wide
    ensureGtmHistoryChangeFilter()
})

const removeModalBackdrop = () => {
    const modalBackdrop = document.querySelector('[modal-backdrop]')

    if (modalBackdrop) {
        modalBackdrop.remove()
        document.body.classList.remove('overflow-hidden')
    }
}

onUnmounted(() => {
    document.body.removeEventListener('wheel', preventHorizontalScroll, { passive: true })
    window.removeEventListener('popstate', removeModalBackdrop)
    window.removeEventListener('show-toast', handleToastEvent)
})

const isFireFox = ref(false)

// Check if the user agent indicates mozilla firefox.
const mozillaPattern = /mozilla\/[.0-9]*/
isFireFox.value = mozillaPattern.test(navigator.userAgent)

function preventHorizontalScroll(event) {
    if (!mozillaPattern && event.deltaX !== 0) {
        event.preventDefault()
    }
}

const pwaDetected = ref(false)

if (isPWA()) {
    pwaDetected.value = true
    console.log('StandAlone App detected.')
    document.body.classList.add('pwa-app')
}

function isPWA() {
    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone ||
        document.referrer.includes('android-app://')
    )
}

const handleDoubleClick = event => {
    event.preventDefault()
    // You can add additional logic here if needed
}

const mainDivRef = ref(null)

const pathNameProp = ref(window.location.pathname)

onMounted(() => {
    pathNameProp.value
})

const isGooglebot = ref(false)

const checkUserAgent = () => {
    const userAgent = navigator.userAgent
    isGooglebot.value = userAgent.includes('Googlebot')
}

onMounted(() => {
    checkUserAgent()
})
</script>

<template>
    <div @contextmenu="$disableRightClick" @dblclick="handleDoubleClick" ref="mainDivRef">
        <div ref="masterLayoutRef" id="masterlayout" class="min-h-dvh h-full bg-gray-100 pb-[70px] dark:bg-black" :class="{ 'feed-page-contain': isFeedPage }">
            <!-- Page Heading -->
            <header class="bg-white shadow" v-if="$slots.header">
                <div class="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                    <slot name="header" />
                </div>
            </header>
            <!-- Page Content -->
            <main
                ref="mainDivRef"
                id="main"
                class="w-full min-h-screen  bg-black flex flex-col">

                <NsfwPopUp v-if="!isLoggedIn" />

                <slot />
            </main>
        </div>

        <Toast
            :show="showToast"
            position="bottomLeft"
            transitionName="fade"
            :transitionEnter="300"
            :transitionLeave="500"
            :text="toastText"
            :icon="toastIcon"
        />
    </div>
</template>
<style>
body {
    background-color: black;
}

div.bg-gray-900.bg-opacity-50 {
    background-color: rgba(0, 0, 0, 0.5);
}

div[drawer-backdrop] {
    display: none;
}

div[drawer-backdrop]:nth-last-child(1) {
    display: block;
}

.feed-page-contain {
    overflow: hidden;
    height: 100dvh !important;
}
</style>
